/**
 * Costi AI (ADR-0030 Slice 2.5) — LLM spend across every AI surface (operator
 * Ask, the Odino builder, the portal voice/chat) in one place. Reads the
 * `ai_usage` ledger aggregates; read-only. Lives in the AI sidebar macro-area.
 */
import { useQuery } from "@tanstack/react-query";
import { Loader2, Sigma } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { ResultChart } from "@/components/charts/ResultChart";
import { PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { aiApi, type UsageBucket } from "@/lib/api-ai";
import { cn } from "@/lib/cn";

const RANGES = [7, 30, 90] as const;

/** USD with 2–4 decimals — sub-cent turns are common, but a month of spend is
 * dollars, so let the formatter pick a sensible precision. */
function usd(n: number): string {
  const digits = n > 0 && n < 1 ? 4 : 2;
  return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: digits });
}

function num(n: number): string {
  return n.toLocaleString();
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-border/60 bg-surface/50 p-4">
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-foreground">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

/** A small "breakdown" table (by surface / by model), sorted by cost desc. */
function Breakdown({ title, rows }: { title: string; rows: UsageBucket[] }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-border/60 bg-surface/50 p-4">
      <div className="mb-2 text-sm font-medium text-foreground">{title}</div>
      {rows.length === 0 ? (
        <p className="py-3 text-xs text-muted-foreground">{t("aiCosts.empty", "Nessun dato nel periodo.")}</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className="pb-1 font-medium">{t("aiCosts.name", "Nome")}</th>
              <th className="pb-1 text-right font-medium">{t("aiCosts.turns", "Turni")}</th>
              <th className="pb-1 text-right font-medium">{t("aiCosts.cost", "Costo")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key ?? "—"} className="border-t border-border/40">
                <td className="py-1.5 font-mono text-xs text-foreground">{r.key ?? "—"}</td>
                <td className="py-1.5 text-right text-muted-foreground">{num(r.turns)}</td>
                <td className="py-1.5 text-right tabular-nums text-foreground">{usd(r.cost_usd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function AiCostsPage() {
  const { t } = useTranslation();
  const [days, setDays] = React.useState<(typeof RANGES)[number]>(30);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["ai", "usage", days],
    queryFn: () => aiApi.usage(days),
  });

  return (
    <PageContainer>
      <PageHeader
        title={t("aiCosts.title", "Costi AI")}
        description={t(
          "aiCosts.subtitle",
          "Spesa LLM stimata su tutte le superfici AI — Chiedi, Odino, voce e chat del portale. Sola lettura.",
        )}
      />
      <PageContent className="space-y-5">
        <div className="flex items-center gap-1.5">
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setDays(r)}
              className={cn(
                "rounded-md border px-2.5 py-1 text-xs transition",
                r === days
                  ? "border-accent/40 bg-muted text-foreground"
                  : "border-border/60 text-muted-foreground hover:bg-muted/50",
              )}
            >
              {t("aiCosts.lastDays", { defaultValue: "{{n}} giorni", n: r })}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> {t("common.loading", "Caricamento…")}
          </div>
        ) : isError || !data ? (
          <p className="py-10 text-sm text-muted-foreground">{t("aiCosts.failed", "Impossibile caricare i costi.")}</p>
        ) : data.totals.turns === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-muted">
              <Sigma className="h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
            </div>
            <p className="max-w-md text-sm text-muted-foreground">
              {t("aiCosts.noSpend", "Nessuna spesa AI registrata nel periodo. Fai una domanda in Chiedi per popolare il registro.")}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <StatCard label={t("aiCosts.totalCost", "Costo totale")} value={usd(data.totals.cost_usd)} />
              <StatCard label={t("aiCosts.turns", "Turni")} value={num(data.totals.turns)} />
              <StatCard
                label={t("aiCosts.inputTokens", "Token input")}
                value={num(data.totals.input_tokens)}
                hint={t("aiCosts.cacheRead", { defaultValue: "{{n}} da cache", n: num(data.totals.cache_read_input_tokens) })}
              />
              <StatCard label={t("aiCosts.outputTokens", "Token output")} value={num(data.totals.output_tokens)} />
            </div>

            {data.by_day.length > 0 && (
              <div className="rounded-xl border border-border/60 bg-surface/50 p-4">
                <div className="mb-1 text-sm font-medium text-foreground">{t("aiCosts.trend", "Spesa per giorno")}</div>
                <ResultChart
                  spec={{ type: "line", x_field: "key", series: ["cost_usd"], title: undefined }}
                  rows={data.by_day as unknown as Record<string, unknown>[]}
                />
              </div>
            )}

            <div className="grid gap-3 lg:grid-cols-2">
              <Breakdown title={t("aiCosts.bySurface", "Per superficie")} rows={data.by_surface} />
              <Breakdown title={t("aiCosts.byModel", "Per modello")} rows={data.by_model} />
            </div>
          </>
        )}
      </PageContent>
    </PageContainer>
  );
}
